/* eslint-disable no-param-reassign */
import { initTheme } from '@front10/helpers/dist/ThemeBuilder';
import * as logger from 'loglevel';
import { annotateAndLogError, getSanitizedError } from '../../../helpers/error';
import { getValue } from '../../../helpers/utils';
import { isSSR } from '@front10/utils';
import { getBusStations } from '../../../_integrations/everymundo/busDirectory';
import { getTranslations, translate } from '../../../_integrations/everymundo/i18n';
import { getCoolContext, getPrepopulation } from '../contextHelper';
import validateCmpConfig from '../validateBusDirectoryCmpConfig';

/**
 * Extract main component metadata to pass it down to the container
 * @param {*} cmpConfig
 */
const getComponentMetaData = (cmpConfig) => {
  return {
    name: cmpConfig.name,
    moduleId: cmpConfig.moduleId,
    version: cmpConfig.version,
    staticAssetsHost: cmpConfig.staticAssetsHost,
    tag: cmpConfig.tag
  };
};

const preloadCustomPropsExecute = (cmpConfig) => {
  return new Promise((resolve, reject) => {
    const cmpConfigValidation = validateCmpConfig(cmpConfig);
    if (!cmpConfigValidation.isValid) {
      logger.error(cmpConfigValidation.message, cmpConfig);
      reject(new Error(cmpConfigValidation.message));
    }
    cmpConfig.pageContext = getCoolContext(cmpConfig.context);
    cmpConfig.containerConfig.settings.prePopulation = getPrepopulation(cmpConfig);
    cmpConfig.containerConfig.settings.meta = getComponentMetaData(cmpConfig);
    const {
      containerConfig: {
        settings: {
          integrationSettings,
          uiDisplay: { showOrigin, showDestination },
          prePopulation: {
            busSearchOptions,
            languageCode,
            siteEdition,
            locationSearchOptions,
            origin,
            destination
          },
          meta,
          interlinkingBaseUrl,
          directionsBaseUrl,
          paginationSettings: { initialVisibleItems }
        }
      },
      pageContext: { airlineIataCode, language },
      pageContext,
      name,
      simulateSSR
    } = cmpConfig;
    // getting the labels
    const labels = getValue(cmpConfig, 'servicesConfig.labels.labels');

    const options = [];
    const commonOptions = {
      defaultOptions: { default: busSearchOptions },
      language: languageCode,
      from: 0,
      step: initialVisibleItems,
      isZipSearch: false
    };

    if (origin && origin.searchCode) {
      if (
        (!busSearchOptions.geoField && origin.geoId) ||
        (busSearchOptions.geoField && origin.proximityGeoId)
      ) {
        options.push({
          ...commonOptions,
          location: origin
        });
      }
    }
    if (destination && destination.searchCode) {
      if (
        (!busSearchOptions.geoField && destination.geoId) ||
        (busSearchOptions.geoField && destination.proximityGeoId)
      ) {
        options.push({
          ...commonOptions,
          location: destination
        });
      }
    }

    if (options.length === 0 && (origin || destination)) {
      annotateAndLogError(
        new Error('Bad prePopulation, location (origin/destination) definitions are incomplete'),
        'INTEGRATION__GREYHOUND__BUS_STATION'
      );
    }

    let promises = [];
    if (!isSSR(simulateSSR)) {
      promises = [
        getTranslations(airlineIataCode, language, integrationSettings, labels),
        pageContext,
        false
      ];
    } else {
      promises = [
        getTranslations(airlineIataCode, language, integrationSettings, labels),
        pageContext,
        // Call api getBusStation to get initial data for SEO
        options.length > 0
          ? getBusStations({
              integrationSettings,
              pageContext,
              options,
              enableRoute: false,
              interlinkingSettings: {
                baseUrl: interlinkingBaseUrl,
                siteEdition,
                pageType: pageContext.pageType,
                showOrigin,
                showDestination,
                filter: locationSearchOptions.outputCategory
              },
              directionsBaseUrl,
              translate,
              meta
            })
          : new Promise((resolveData) => resolveData({ data: [] }))
      ];
    }

    Promise.all(promises)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then(([translations, myPageContext, busStationsData]) => {
        // Get initial bus stations
        cmpConfig.containerConfig.settings.initialBusStations = busStationsData.data;
        cmpConfig.containerConfig.settings.translate = translate;
        cmpConfig.containerConfig.settings.pageContext = myPageContext;
        const themeConfig = { ...cmpConfig.containerConfig.theme };
        delete cmpConfig.containerConfig.theme;
        const theme = initTheme(themeConfig);
        cmpConfig.containerConfig.settings.theme = theme;
        resolve(cmpConfig);
      })
      .catch((err) => {
        logger.error(`PRELOAD_CUSTOM_PROPS: ${name}`, err);
        reject(err);
      });
  });
};

const preloadCustomProps = (cmpConfig) => {
  return new Promise((resolve) => {
    preloadCustomPropsExecute(cmpConfig)
      .then((resultCmpConfig) => {
        resolve(resultCmpConfig);
      })
      .catch((err) => {
        const ERROR = annotateAndLogError(err, `PRELOAD_CUSTOM_PROPS`, cmpConfig);
        if (!cmpConfig) {
          cmpConfig = {};
        }
        if (!cmpConfig.containerConfig) {
          cmpConfig.containerConfig = {};
        }
        if (!cmpConfig.containerConfig) {
          cmpConfig.containerConfig = {};
        }
        cmpConfig.containerConfig.criticalError = getSanitizedError(ERROR);
        resolve(cmpConfig);
      });
  });
};
export default preloadCustomProps;
